import * as React from 'react';

/**
 * Context.
 */
import { RadioGroupContext } from '../context';

/**
 * Components.
 */
import Typography from '../../commonComponents/Typography/Typography';

/**
 * Styles
 */
import RadioGroupContainer, {
  BaseStyleProps,
  RadioGroupWrapper
} from './RadioGroup.style';

export interface IRadioGroupProps extends BaseStyleProps {
  /**
   * Sets prefixed ID for fields within form
   */
  name: string;
  /**
   * string value to set the section heading label of the radio group
   */
  headingLabel: string;

  /**
   * Whether the group is entirely disabled or not.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the form elements should be stacked vertically.
   *
   * @default false
   */
  stacked?: boolean;

  /**
   * function Event listener that handles change
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  children?: React.ReactNode;
}

/**
 * Group of related `Form.RadioBox` elements where only one
 * can be selected.
 */
const RadioGroup: React.FC<IRadioGroupProps> = ({
  children,
  headingLabel,
  name,
  onChange,
  disabled = false,
  stacked = false,
  ...props
}) => {
  return (
    <RadioGroupContext.Provider
      value={{
        groupId: name,
        groupDisabled: disabled,
        groupOnChange: onChange
      }}
    >
      <RadioGroupWrapper as="fieldset" {...props}>
        <Typography
          kind="subtitle3"
          display="block"
          color={
            disabled ? 'form.text.label.disabled' : 'form.text.label.enabled'
          }
          m={0}
          mb={2}
          p={0}
          id={name}
        >
          {headingLabel}
        </Typography>
        <RadioGroupContainer
          flexWrap="wrap"
          flexDirection={stacked ? 'column' : 'row'}
          disabled={disabled}
        >
          {children}
        </RadioGroupContainer>
      </RadioGroupWrapper>
    </RadioGroupContext.Provider>
  );
};

export default RadioGroup;
